import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { ActionCenter } from '../components/action-center';
import { money, shortDate } from '../lib/format';
import type { Application, ArtistProfile, ListResponse, Order } from '../types';
import { Card, ErrorNotice, PageHeader } from '../components/ui';

function orderWord(count: number): string {
  const tens = count % 100;
  const ones = count % 10;

  if (tens >= 11 && tens <= 14) return 'заказов';
  if (ones === 1) return 'заказ';
  if (ones >= 2 && ones <= 4) return 'заказа';
  return 'заказов';
}

export function DashboardPage({ profile }: { profile: ArtistProfile }) {
  const queryClient = useQueryClient();
  const applications = useQuery({
    queryKey: ['applications', 'attention'],
    queryFn: () => api<ListResponse<Application>>('/applications?limit=100'),
  });
  const orders = useQuery({
    queryKey: ['orders', 'active'],
    queryFn: () => api<ListResponse<Order>>('/orders?active=true&limit=100'),
  });
  const toggleStatus = useMutation({
    mutationFn: (status: ArtistProfile['commissionsStatus']) =>
      api<ArtistProfile>('/artist/commissions-status', {
        method: 'PATCH',
        body: { status },
      }),
    onSuccess: (next) => queryClient.setQueryData(['profile'], next),
  });

  const activeOrders = orders.data?.items ?? [];
  const attention = (applications.data?.items ?? []).filter((item) =>
    ['NEW', 'NEEDS_INFO'].includes(item.status),
  );
  const nextDeadline = [...activeOrders]
    .filter((order) => order.deadline && order.deadlineState !== 'OVERDUE')
    .sort((a, b) => String(a.deadline).localeCompare(String(b.deadline)))[0];
  const currencyTotals = [
    ...activeOrders.reduce((totals, order) => {
      const current = totals.get(order.currency) ?? { balance: 0, booked: 0 };
      current.balance += Number(order.payment.balanceDue);
      current.booked += Number(order.totalPrice);
      totals.set(order.currency, current);
      return totals;
    }, new Map<string, { balance: number; booked: number }>()),
  ].map(([currency, totals]) => ({
    currency,
    ...totals,
  }));
  const featuredCurrency = currencyTotals.find(
    (item) => item.currency === profile.currency && item.balance > 0,
  ) ??
    currencyTotals.find((item) => item.balance > 0) ??
    currencyTotals.find((item) => item.currency === profile.currency && item.booked > 0) ??
    currencyTotals[0] ?? {
      currency: profile.currency,
      balance: 0,
      booked: 0,
    };
  const otherCurrencies = currencyTotals.filter(
    (item) => item.currency !== featuredCurrency.currency && item.balance > 0,
  );
  const receivableSummary =
    otherCurrencies.length > 0
      ? `Ещё: ${otherCurrencies.map((item) => money(item.balance, item.currency)).join(' · ')}`
      : `из ${money(featuredCurrency.booked, featuredCurrency.currency)}`;

  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div className="dashboard-avatar" aria-hidden="true">
          {profile.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : <span />}
        </div>
        <PageHeader
          eyebrow={new Intl.DateTimeFormat('ru-RU', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          }).format(new Date())}
          title={`Привет, ${profile.displayName.split(' ')[0]}`}
        />
      </div>

      <Card className="commissions-card">
        <div>
          <h2>Приём заказов</h2>
          <p>
            {profile.commissionsStatus === 'OPEN'
              ? 'Открыт — можно принимать новые заявки'
              : profile.commissionsStatus === 'WAITLIST'
                ? 'Открыт лист ожидания'
                : 'Новые заявки временно закрыты'}
          </p>
        </div>
        <button
          className={`switch ${profile.commissionsStatus !== 'CLOSED' ? 'is-on' : ''}`}
          type="button"
          role="switch"
          aria-checked={profile.commissionsStatus !== 'CLOSED'}
          aria-label="Переключить приём заказов"
          disabled={toggleStatus.isPending}
          onClick={() =>
            toggleStatus.mutate(profile.commissionsStatus === 'CLOSED' ? 'OPEN' : 'CLOSED')
          }
        >
          <span />
        </button>
      </Card>

      <div className="metrics-grid">
        <Link href="/orders" className="metric metric--surface">
          <span>Активные заказы</span>
          <strong>{activeOrders.length}</strong>
          <small>
            {activeOrders.filter((order) => order.status === 'IN_PROGRESS').length} в работе
          </small>
        </Link>
        <Link href="/applications" className="metric metric--accent">
          <span>Новые заявки</span>
          <strong>{attention.length}</strong>
          <small>требуют ответа</small>
        </Link>
        <div className="metric metric--surface metric--receivable">
          <span>К получению</span>
          <strong>{money(featuredCurrency.balance, featuredCurrency.currency)}</strong>
          <small title={receivableSummary}>{receivableSummary}</small>
        </div>
        <Link
          href={nextDeadline ? `/orders/${nextDeadline.id}` : '/calendar'}
          className="metric metric--surface"
        >
          <span>Ближайший срок</span>
          <strong>{nextDeadline ? shortDate(nextDeadline.deadline) : '—'}</strong>
          <small>
            {nextDeadline
              ? `${nextDeadline.publicNumber} · ${nextDeadline.client.displayName}`
              : 'сроков нет'}
          </small>
        </Link>
      </div>

      {(orders.error || applications.error || toggleStatus.error) && (
        <ErrorNotice error={orders.error ?? applications.error ?? toggleStatus.error} />
      )}

      <ActionCenter />

      <div className="dashboard-shortcuts">
        <Link href="/calendar" className="card calendar-cta">
          <div>
            <p className="eyebrow">Планирование</p>
            <h3>Календарь</h3>
            <p className="muted">
              {activeOrders.length > 0
                ? `${activeOrders.length} ${orderWord(activeOrders.length)} на ленте`
                : 'Посмотрите, что и когда вы успеваете'}
            </p>
          </div>
          <span className="chevron">›</span>
        </Link>

        <Link href="/clients" className="card calendar-cta">
          <div>
            <p className="eyebrow">База</p>
            <h3>Клиенты</h3>
            <p className="muted">Кто к вам возвращается, что заказывал и сколько заплатил</p>
          </div>
          <span className="chevron">›</span>
        </Link>

        <Link href="/analytics" className="card calendar-cta">
          <div>
            <p className="eyebrow">Отчёт</p>
            <h3>Деньги и работа</h3>
            <p className="muted">Доход, средний чек, просрочки и выгрузка в таблицу</p>
          </div>
          <span className="chevron">›</span>
        </Link>
      </div>
    </div>
  );
}
